import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";

import { Close } from "@material-ui/icons";
import {
  getPracticeClaimCPTRecommendationDetails,
  getPracticeClaimICDRecommendationDetails,
  updatePracticeClaimCPTRecommendationDetails,
  updatePracticeClaimICDRecommendationDetails,
} from "apiHandler/actions";

import {
  cptLeftColumns,
  cptRightColumns,
  icdLeftColumns,
  icdRightColumns,
} from "./helper";
import NoteDialog from "./noteDialog";
import CodeTable from "./codeTable";
import ClaimPatientInfo from "./claimPatientInfo";

const collapseSections = [
  "clinical_justification",
  "supporting_evidence",
  "documentation_requirements",
  "user_decision_notes",
];

const ClaimDetails = ({ open, onClose, claim, section, userData }) => {
  const showICD = section === "ICD";
  const showCPT = section === "CPT";

  const [originalRows, setOriginalRows] = useState([]);
  const [aiRows, setAiRows] = useState([]);
  const [aiPriRows, setAiPriRows] = useState([]);
  const [aiSecRows, setAiSecRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [note, setNote] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const token = userData?.access_token;

  useEffect(() => {
    if (open && claim) {
      getClaimDetails();
    }
  }, [open, claim?.claim_id, section]);

  const getClaimDetails = async () => {
    setLoading(true);
    setAiRows([]);
    setOriginalRows([]);
    setAiPriRows([]);
    setAiSecRows([]);
    try {
      const payload = {
        practice_id: userData?.selected_practice,
        claim_no: `${claim?.claim_no}`,
      };

      let res;
      if (showICD) {
        res = await getPracticeClaimICDRecommendationDetails(payload, token);
      } else if (showCPT) {
        res = await getPracticeClaimCPTRecommendationDetails(payload, token);
      }

      const originalData = res.filter(
        (item) => item.recommendation_type === "original"
      );
      const aiData = res.filter(
        (item) => item.recommendation_type !== "original"
      );

      const aiPrimaryCodes = aiData.filter((row) => Number(row.rvu_value) > 0);

      const aiQualityCodes = aiData.filter(
        (row) => Number(row.rvu_value) === 0
      );

      setAiRows(aiData);
      setOriginalRows(originalData);

      setAiPriRows(aiPrimaryCodes);

      setAiSecRows(aiQualityCodes);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (row, action) => {
    setAction(action);
    setSelectedRow(row);
    setOpenSubmit(true);
  };

  const closeSubmit = () => {
    setOpenSubmit(false);
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        practice_id: userData?.selected_practice,
        recommendation_id: selectedRow?.recommendation_id,
        user_decision: action,
        user_name: userData.first_name + " " + userData.last_name,
        ...(note !== "" && { user_decision_notes: note }),
      };
      let res;
      if (showICD) {
        res = await updatePracticeClaimICDRecommendationDetails(payload, token);
      } else if (showCPT) {
        res = await updatePracticeClaimCPTRecommendationDetails(payload, token);
      }
      if (res && res?.status === "successfull") {
        setSnackbarSeverity("success");
        setSnackbarMessage("Submission successful");
        setSnackbarOpen(true);
        getClaimDetails();
        setNote("");
      }

      closeSubmit();
    } catch (error) {
      console.log("error: ", error);
      setSnackbarSeverity("error");
      setSnackbarMessage("Submission failed. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xl"
      onClose={onClose}
      aria-labelledby="claim-detail-title"
    >
      <DialogContent>
        <Box
          display="flex"
          alignItems="center"
          mb={2}
          justifyContent="space-between"
        >
          <Typography variant="h6" id="claim-detail-title">
            Claim Detail ({claim?.claim_no}) - {section} Section
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <ClaimPatientInfo
          practiceID={userData?.selected_practice}
          token={token}
          claim={claim}
          showICD={showICD}
          showCPT={showCPT}
          setSnackbarSeverity={setSnackbarSeverity}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarOpen={setSnackbarOpen}
        />

        {showICD && (
          <>
            <CodeTable
              title="Original Codes"
              rows={originalRows}
              columns={icdLeftColumns}
              loading={loading}
              collapseSections={collapseSections}
            />
            <CodeTable
              title="AI Codes"
              rows={aiRows}
              columns={icdRightColumns(handleUpdate)}
              loading={loading}
              collapseSections={collapseSections}
            />
          </>
        )}
        {showCPT && (
          <>
            <CodeTable
              title="Original Codes"
              rows={originalRows}
              columns={cptLeftColumns}
              loading={loading}
              collapseSections={collapseSections}
            />
            <CodeTable
              title="AI Primary Codes"
              rows={aiPriRows}
              columns={cptRightColumns(handleUpdate)}
              loading={loading}
              collapseSections={collapseSections}
            />

            <CodeTable
              title="AI Quality Codes"
              rows={aiSecRows}
              columns={cptRightColumns(handleUpdate)}
              loading={loading}
              collapseSections={collapseSections}
            />
          </>
        )}

        <NoteDialog
          open={openSubmit}
          onClose={closeSubmit}
          note={note}
          setNote={setNote}
          onSubmit={onSubmit}
          submitting={submitting}
          showICD={showICD}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimDetails;
